import { FC, useState, useEffect, useMemo, useRef } from "react";
import { Data, UserData } from "../../../types/org.type";
import { useStyles } from "./InfoDocWallex.styles";
import { toast } from "react-toastify";
import useGetDocument from "@funded-here-interface/common/src/hooks/useGetDocument";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/admin-fundedhere/src/store";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import { FileType } from "@funded-here-interface/common/src/constant/enum";
import { useLocation } from "react-router-dom";
import { Button } from "@mantine/core";
import { Divider, Text } from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";
import {
  mapCategoryToDocumentType,
  formatLocalDate,
  getNameFromPath,
  getLocalPhotoIdentifier,
} from "./Components/utils";
import {
  wallexInfoUpload,
  uploadWallexDocuments,
} from "./Service/ApiServices";
import { ScreeningStatus } from "./Components/Enum";
import Forms from "./Components/Forms";
import { useWallexStatus } from "./hooks/useWallexStatus";
import { useRegtankPhotos } from "./hooks/useRegtankPhotos";
import { useIdentityPreview } from "./hooks/useIdentityPreview";
import IdentityPreviewModal from "./Components/IdentityPreviewModal";
import WallexConfirmModal from "./Components/WallexConfirmModal";
import NationalIdentitySection from "./Components/NationalIdentitySection";
import { DocumentRenderer } from "./utils/documentRenderer";
import { getDirectorUsers } from "./utils/getDirectorUsers";
import {
  buildDerivedValues,
  buildWallexPayload,
} from "./utils/wallexPayloadBuilder";
import { documentConfigs, getDocumentHeader } from "./documentConfig";

interface IProps {
  userData: UserData | undefined;
  user: UserData[] | undefined;
  data: Data | undefined;
}

export interface WallexUploadError {
  message?: string;
  response?: {
    data?: {
      message?: string | string[];
      source?: string;
      errors?: Record<string, string[]>;
      failedDocuments?: Array<{
        fileName?: string;
        errorMessage?: string;
        error?: string;
        supportMessage?: string;
      }>;
      results?: Array<{ status: string; fileName: string }>;
    };
    messages?: string;
  };
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/pdf",
];

const InfoDocWallex: FC<IProps> = ({ userData, data, user }) => {
  //For PopUp message
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<WallexUploadError | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const {
    identityPreviewOpen,
    identityPreviewContent,
    identityPreviewType,
    identityPreviewFileName,
    openIdentityPreview,
    closeIdentityPreview,
  } = useIdentityPreview();
  const { classes } = useStyles();
  const location = useLocation();
  const isOrgRoute =
    location.pathname.includes("/org-") || location.pathname.includes("/org/");
  const isIndividualRoute =
    location.pathname.includes("/individual-") ||
    location.pathname.includes("/individual/");
  const [localPassportPhotos, setLocalPassportPhotos] = useState<
    Record<
      string,
      Array<{
        file: File;
        previewUrl: string;
        binaryData?: ArrayBuffer;
      }>
    >
  >({});
  const blobUrlsRef = useRef<string[]>([]);
  const userId = userData?.id;
  const useGetDocumentMutation = useGetDocument();
  // Initialize document paths from data for all configured document types
  const [documentPaths, setDocumentPaths] = useState(() => {
    const paths: Record<string, string[]> = {};
    documentConfigs.forEach((config) => {
      paths[config.field] = data?.documentsUpload?.[config.field] || [];
    });
    return paths;
  });

  const token = useSelector((state: RootState) => state.auth.token);

  // Wallex status hook — owns all status state + helpers
  const {
    screeningStatus,
    screeningRemarks,
    hasBeenSentToWallex,
    uploadedFiles,
    uploadPartialSuccess,
    failedDocuments,
    wallexPassportPhotos,
    excludedFiles,
    lastUploadTimestamp,
    setHasBeenSentToWallex,
    setUploadPartialSuccess,
    setFailedDocuments,
    setExcludedFiles,
    setUploadedFiles,
    processSuccessfulUploads,
    refreshPassportPhotos,
    refreshDataFromServer,
  } = useWallexStatus({ userId, user, token });

  // Regtank passport photos hook
  const { regtankDocumentPhoto, passportPhotoIsLoading } = useRegtankPhotos({
    user,
    token,
    hasBeenSentToWallex,
  });

  useEffect(() => {
    const paths: Record<string, string[]> = {};
    documentConfigs.forEach((config) => {
      paths[config.field] = data?.documentsUpload?.[config.field] || [];
    });
    setDocumentPaths(paths);
  }, [data]);

  // Revoke all blob URLs on unmount to prevent memory leaks
  useEffect(() => {
    const urls = blobUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // Function to handle local passport photos
  const handlePassportPhotoUpload = (
    userId: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Process all selected files
    Array.from(files).forEach((file) => {
      // Validate file size and type before processing
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`File "${file.name}" exceeds 10 MB limit.`);
        return;
      }
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        toast.error(`File "${file.name}" has unsupported type "${file.type}".`);
        return;
      }

      // Create a preview URL for display only
      const previewUrl = URL.createObjectURL(file);
      blobUrlsRef.current.push(previewUrl); // Track for cleanup

      const reader = new FileReader();
      reader.onload = () => {
        // Store the file, preview URL, and binary data
        // Only use the result if it's an ArrayBuffer
        setLocalPassportPhotos((prev) => ({
          ...prev,
          [userId]: [
            ...(prev[userId] || []),
            {
              file,
              previewUrl,
              binaryData:
                reader.result instanceof ArrayBuffer
                  ? reader.result
                  : undefined,
            },
          ],
        }));
      };

      // Read the file as an ArrayBuffer (binary data)
      reader.readAsArrayBuffer(file);
    });

    // Reset the file input so the same file can be selected again
    event.target.value = "";
  };

  const derivedValues = useMemo(
    () => buildDerivedValues({ data, userData, isOrgRoute, isIndividualRoute }),
    [data, userData, isOrgRoute, isIndividualRoute]
  );

  const {
    firstName,
    lastName,
    email,
    gender,
    mobileCountryCode,
    mobileNumber,
    employmentStatus,
    employmentIndustry,
    employmentPosition,
    dateOfBirth,
    countryOfBirth,
    address,
    postalCode,
    countryCode,
    nationality,
    identificationType,
    identificationNumber,
    issueDate,
    expiryDate,
    companyName,
    registrationNumber,
    businessType,
    displayIncorporationDate,
    city,
    state,
    countryOfDomicile,
    countryOfIncorporation,
    customerBusinessWebsite,
    customerBusinessDescription,
    entityType,
    ownershipStructureLayers,
    paymentModes,
    onboardingMode,
    productServiceComplexity,
    companyAddress,
    companyPostalCode,
    isPermanentPlaceOfBusiness,
    siteVisitPerformedAtAddress,
    isPremisesInLineWithBusiness,
    isHigherRiskEntityType,
    higherRiskEntityType,
    productUsage,
    purposeOfAccountOpening,
    isMakingPaymentsToIndividuals,
    makingPaymentsToIndividualsDescription,
    isMakingThirdPartyPayments,
    sourceOfFunds,
    primaryTransactingCountriesInbound,
    primaryTransactingCountriesOutbound,
    topCounterPartiesInbound,
    topCounterPartiesOutbound,
    anticipatedVolumeMonthlyTransactionsCountInbound,
    anticipatedVolumeMonthlyTransactionsCountOutbound,
    anticipatedValueMonthlyTransactionsInbound,
    anticipatedValueMonthlyTransactionsOutbound,
    currencyPairsInbound,
    currencyPairsOutbound,
  } = derivedValues;

  const toggleExcludeDocument = (path: string) => {
    if (excludedFiles.includes(path)) {
      // If already excluded, restore it
      setExcludedFiles((prev) => prev.filter((p) => p !== path));
      //toast.success("Document restored.");
    } else {
      // Mark as excluded
      setExcludedFiles((prev) => [...prev, path]);
      //toast.success("Document marked as excluded from upload.");
    }
  };

  const toggleExcludePassportPhoto = (
    userId: string,
    pathFront: string,
    pathBack: string,
    isLocal: boolean = false
  ) => {
    const uniqueIdentifierFront = isLocal
      ? getLocalPhotoIdentifier(userId)
      : `user_${userId}_photo_${pathFront}`;
    const uniqueIdentifierBack = isLocal
      ? getLocalPhotoIdentifier(userId)
      : `user_${userId}_photo_${pathBack}`;
    if (
      excludedFiles.includes(uniqueIdentifierFront) &&
      excludedFiles.includes(uniqueIdentifierBack)
    ) {
      // If already excluded, restore both in a single state update
      setExcludedFiles((prev) =>
        prev.filter((p) => p !== uniqueIdentifierFront && p !== uniqueIdentifierBack)
      );
    } else {
      // Mark both as excluded in a single state update
      setExcludedFiles((prev) => [...prev, uniqueIdentifierFront, uniqueIdentifierBack]);
    }
  };

  const removeLocalPassportPhoto = (userId: string, fileIndex: number) => {
    setLocalPassportPhotos((prev) => {
      const currentFiles = prev[userId] || [];
      // Revoke the preview URL to free memory
      if (currentFiles[fileIndex]?.previewUrl) {
        URL.revokeObjectURL(currentFiles[fileIndex].previewUrl);
      }
      const updatedFiles = currentFiles.filter(
        (_, index) => index !== fileIndex
      );
      if (updatedFiles.length === 0) {
        const newState = { ...prev };
        delete newState[userId];
        return newState;
      }
      return {
        ...prev,
        [userId]: updatedFiles,
      };
    });
  };

  const downloadDocument = (path: string, fileName: string) => {
    useGetDocumentMutation.mutate(
      { token, path },
      {
        onSuccess: (data: Blob) => {
          downloadFile(data, fileName, FileType.PDF);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  const performUpload = async () => {
    setIsUploading(true);
    setUploadError(null);
    if (uploadPartialSuccess) {
      await documentUploadWallex();
    } else {
      await wallexUpload();
    }
  };

  // Update UI state after successful upload
  const handleUploadSuccess = () => {
    setIsUploading(false);
    setIsCompleted(true);
    setHasBeenSentToWallex(true);
    setUploadPartialSuccess(false);
  };

  // Handle upload errors with appropriate state updates
  const handleUploadError = (error: WallexUploadError) => {
    if ((error?.response?.data?.failedDocuments?.length ?? 0) > 0) {
      setFailedDocuments(error.response!.data!.failedDocuments!);
      processSuccessfulUploads(error?.response?.data?.results);
      setUploadPartialSuccess(true);
      setHasBeenSentToWallex(false);
      refreshPassportPhotos();
    }

    setUploadError(error);
    setIsUploading(false);
    console.error("Document upload failed:", error);
  };

  // Function to upload user/company information and documents to Wallex
  // Errors bubble to onConfirm's catch → handleUploadError; no duplicate catch needed
  const wallexUpload = async () => {
    const payloadData = buildWallexPayload(
      derivedValues,
      userId,
      isIndividualRoute,
      excludedFiles
    );

    await wallexInfoUpload(payloadData, token);

    // Call document upload function
    await documentUploadWallex();

    // If we get here, everything was successful
    setHasBeenSentToWallex(true);
    setUploadPartialSuccess(false);
    setFailedDocuments([]);
  };

  // Function to upload documents only to Wallex
  const documentUploadWallex = async () => {
    try {
      const uploadedFileNames: string[] = [];
      const documentsToSubmit = structuredClone(documentPaths);
      const documentsArray = [];

      // Process each document category
      for (const category of Object.keys(documentsToSubmit)) {
        // Filter excluded files
        const filesToUpload = documentsToSubmit[category].filter(
          (path: string) => !excludedFiles.includes(path)
        );

        // Add each document to the array
        for (const filePath of filesToUpload) {
          const fileName = getNameFromPath(filePath);
          const fileNameOnly = fileName.split("\\").pop() || fileName;
          // Determine content type based on file extension
          const fileExtension =
            fileName.split(".").pop()?.toLowerCase() || "pdf";
          let contentType = "application/pdf";
          if (fileExtension === "jpg" || fileExtension === "jpeg") {
            contentType = "image/jpeg";
          } else if (fileExtension === "png") {
            contentType = "image/png";
          }
          documentsArray.push({
            fileName: String(fileNameOnly),
            documentType: String(
              mapCategoryToDocumentType(category, isIndividualRoute)
            ),
            contentType: contentType,
            path: filePath,
            userId: userId,
          });

          // Add to our tracking array
          uploadedFileNames.push(fileNameOnly);
        }
      }

      // Add passport photos that aren't excluded
      const usersToProcess = getDirectorUsers(
        user,
        data?.boardOfDirectors,
        isOrgRoute
      );

      if (usersToProcess.length) {
        usersToProcess.forEach((currentUser) => {
          const userId = currentUser.id.toString();
          const uniqueIdentifier = `user_${userId}_photo_${currentUser.didPassportPhoto}`;
          const localPhotoDataArray = localPassportPhotos[userId];

          // First: include locally uploaded photos if available and not excluded
          if (localPhotoDataArray && localPhotoDataArray.length > 0) {
            localPhotoDataArray.forEach((localPhotoData, index) => {
              // Create filename using the local file's name or a default name with the user ID
              const fileBaseName =
                localPhotoData.file.name.split(".")[0] ||
                `passport_${index + 1}`;
              const fileExtension =
                localPhotoData.file.name.split(".").pop() || "jpg";
              const passportFileName = `${fileBaseName}.${fileExtension}`;
              // Chunked base64 encoding to handle large files without hitting argument size limits
              let base64String = "";
              if (localPhotoData.binaryData) {
                const bytes = new Uint8Array(localPhotoData.binaryData as ArrayBuffer);
                const CHUNK_SIZE = 8192;
                let binary = "";
                for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
                  binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK_SIZE)));
                }
                base64String = btoa(binary);
              }

              documentsArray.push({
                fileName: passportFileName,
                documentType: "national_identity",
                contentType: localPhotoData.file.type || "application/pdf",
                fileBuffer: base64String,
                userId: userId,
                passportUserId: userData?.id,
              });
            });
          } else if (regtankDocumentPhoto.length > 0) {
            const matchRegtank = regtankDocumentPhoto.find(
              (x) => x.userId === currentUser.id
            );
            // Second priority: use existing photo if not excluded
            if (
              matchRegtank?.frontDocumentPhoto &&
              matchRegtank.backDocumentPhoto &&
              !excludedFiles.includes(uniqueIdentifier)
            ) {
              let base64DataFront = matchRegtank.frontDocumentPhoto;
              let fileTypeFront = "";
              if (base64DataFront.startsWith("data:")) {
                // Extract just the base64 part after the comma
                base64DataFront = base64DataFront.split(",")[1];
                fileTypeFront = matchRegtank.frontDocumentPhoto
                  .split(";")[0]
                  .split("/")[1];
              }
              let base64DataBack = matchRegtank.backDocumentPhoto;
              let fileTypeBack = "";
              if (base64DataBack.startsWith("data:")) {
                // Extract just the base64 part after the comma
                base64DataBack = base64DataBack.split(",")[1];
                fileTypeBack = matchRegtank.backDocumentPhoto
                  .split(";")[0]
                  .split("/")[1];
              }

              documentsArray.push({
                fileName: `National_Identity_Front.${fileTypeFront}`,
                documentType: "national_identity",
                contentType: `image/${fileTypeFront}`,
                fileBuffer: base64DataFront,
                userId: userId,
                passportUserId: userData?.id,
              });
              documentsArray.push({
                fileName: `National_Identity_Back.${fileTypeBack}`,
                documentType: "national_identity",
                contentType: `image/${fileTypeBack}`,
                fileBuffer: base64DataBack,
                userId: userId,
                passportUserId: userData?.id,
              });
            }
          }
        });
      }
      // API call for document upload only
      if (documentsArray.length > 0) {
        await uploadWallexDocuments(documentsArray, token);
        setUploadedFiles(uploadedFileNames);
      }
    } catch (error) {
      console.error("Document upload error:", error);
      throw error;
    }
  };

  const screeningStatusConfig: Record<string, { label: string; color: string }> = {
    approved: { label: "Approved", color: "#2e7d32" },
    pending_docs: { label: "Pending Documents", color: "#ff9800" },
    pending_approval: { label: "Pending Approval", color: "#ff9800" },
    documents_need_resubmission: { label: "Documents need resubmission", color: "#f57c00" },
    rejected: { label: "Rejected", color: "#d32f2f" },
    suspended: { label: "Suspended", color: "#d32f2f" },
    inactive: { label: "Inactive", color: "#757575" },
    dormant: { label: "Dormant", color: "#757575" },
  };

  const currentScreeningConfig = screeningStatusConfig[screeningStatus ?? ""] ?? {
    label: "Pending",
    color: "#757575",
  };

  return (
    <div>
      {/* Wallex Onboarding Status - Shows when hasBeenSentToWallex is true OR screeningStatus is "documents_need_resubmission" */}
      {(hasBeenSentToWallex ||
        screeningStatus === "documents_need_resubmission" ||
        screeningStatus === ScreeningStatus.PENDING_DOCS) && (
        <div
          style={{
            marginTop: "10px",
            marginBottom: "-10px",
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <div
            style={{
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              padding: "10px 15px",
              border: "1px solid #dee2e6",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Text weight={500} size="md">
              Wallex Onboarding Status:{" "}
              <Text
                style={{ color: currentScreeningConfig.color }}
                span
                weight={600}
              >
                {currentScreeningConfig.label}
              </Text>
            </Text>

            {/* Remarks section */}
            {(screeningStatus === "rejected" ||
              screeningStatus === "documents_need_resubmission" ||
              screeningStatus === ScreeningStatus.PENDING_DOCS) &&
              screeningRemarks && (
                <div
                  style={{
                    marginTop: "10px",
                    fontSize: "14px",
                    color: "#d32f2f",
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  <Text weight={500} color="black">
                    Remarks:{" "}
                    <span style={{ color: "#d32f2f" }}>{screeningRemarks}</span>
                  </Text>
                </div>
              )}
          </div>
        </div>
      )}
      <div className={classes.sectionHeader}>
        <b>
          {hasBeenSentToWallex
            ? "Onboarding Information Sent to Wallex"
            : "Onboarding Information to Wallex"}
        </b>
      </div>
      <div className={classes.parentContainer}>
        <div className={classes.valueContainer}>
          <div className={classes.companyInfoContainer}>
            <Forms.UserSignUpDetails
              accountType={isIndividualRoute ? "individual" : "company"}
              email={email}
              firstName={firstName}
              lastName={lastName}
              companyName={companyName}
              countryCode={countryCode}
            />

            <Forms.UserDetails
              firstName={firstName}
              lastName={lastName}
              gender={gender}
              mobileCountryCode={mobileCountryCode}
              mobileNumber={mobileNumber}
              employmentStatus={employmentStatus}
              employmentIndustry={employmentIndustry}
              employmentPosition={employmentPosition}
              dateOfBirth={dateOfBirth}
              countryOfBirth={countryOfBirth}
              residentialAddress={address}
              postalCode={postalCode}
              countryCode={countryCode}
              nationality={nationality}
              identificationType={identificationType}
              identificationNumber={identificationNumber || "-"}
              issueDate={issueDate}
              expiryDate={expiryDate}
            />

            {isOrgRoute && (
              <>
                <Forms.CompanyDetails
                  companyName={companyName}
                  businessType={businessType}
                  registrationNumber={registrationNumber}
                  incorporationDate={displayIncorporationDate}
                  companyAddress={companyAddress}
                  companyPostalCode={companyPostalCode}
                  city={city}
                  state={state}
                  countryOfIncorporation={countryOfIncorporation || "-"}
                  countryOfOperation={countryOfDomicile || "-"}
                />
                <Forms.CustomerDueDiligence
                  customerBusinessWebsite={customerBusinessWebsite}
                  customerBusinessDescription={customerBusinessDescription}
                  entityType={entityType}
                  ownershipStructureLayers={ownershipStructureLayers}
                  countryOfIncorporation={countryOfIncorporation || "-"}
                  countryOfOperation={countryOfDomicile || "-"}
                  paymentModes={paymentModes}
                  onboardingMode={onboardingMode}
                  productServiceComplexity={productServiceComplexity}
                  companyAddress={companyAddress}
                  isPermanentPlaceOfBusiness={isPermanentPlaceOfBusiness}
                  siteVisitPerformedAtAddress={siteVisitPerformedAtAddress}
                  isPremisesInLineWithBusiness={isPremisesInLineWithBusiness}
                  isHigherRiskEntityType={isHigherRiskEntityType}
                  higherRiskEntityType={higherRiskEntityType}
                  productUsage={productUsage}
                  purposeOfAccountOpening={purposeOfAccountOpening}
                  isMakingPaymentsToIndividuals={isMakingPaymentsToIndividuals}
                  makingPaymentsToIndividualsDescription={
                    makingPaymentsToIndividualsDescription
                  }
                  isMakingThirdPartyPayments={isMakingThirdPartyPayments}
                  sourceOfFunds={sourceOfFunds}
                  primaryTransactingCountriesInbound={
                    primaryTransactingCountriesInbound
                  }
                  primaryTransactingCountriesOutbound={
                    primaryTransactingCountriesOutbound
                  }
                  topCounterPartiesInbound={topCounterPartiesInbound}
                  topCounterPartiesOutbound={topCounterPartiesOutbound}
                  anticipatedVolumeMonthlyTransactionsCountInbound={
                    anticipatedVolumeMonthlyTransactionsCountInbound
                  }
                  anticipatedVolumeMonthlyTransactionsCountOutbound={
                    anticipatedVolumeMonthlyTransactionsCountOutbound
                  }
                  anticipatedValueMonthlyTransactionsInbound={
                    anticipatedValueMonthlyTransactionsInbound
                  }
                  anticipatedValueMonthlyTransactionsOutbound={
                    anticipatedValueMonthlyTransactionsOutbound
                  }
                  currencyPairsInbound={currencyPairsInbound}
                  currencyPairsOutbound={currencyPairsOutbound}
                />
              </>
            )}
          </div>
        </div>
      </div>
      <div className={classes.sectionHeader}>
        <Divider my="md" className={classes.divider} />
        <b>
          {hasBeenSentToWallex
            ? "Onboarding Documents Sent to Wallex"
            : "Onboarding Documents to Wallex"}
        </b>
      </div>
      <div className={classes.valueContainer}>
        <div className={classes.docContainer}>
          {/* Render documents based on centralized configuration */}
          {documentConfigs
            .filter((config) => {
              // Only show documents that are relevant for this context
              // InfoDocWallex currently shows: businessProfile, shareHoldersDirectorsRegisters, proofOfAddress
              const allowedFields = [
                "businessProfile",
                "shareHoldersDirectorsRegisters",
                "proofOfAddress",
              ];
              return allowedFields.includes(config.field);
            })
            .map((config) => {
              const documents = documentPaths[config.field] || [];
              return (
                <DocumentRenderer
                  key={config.field}
                  documents={documents}
                  header={getDocumentHeader(config, data)}
                  fileType={config.fileType}
                  hasBeenSentToWallex={hasBeenSentToWallex}
                  uploadedFiles={uploadedFiles}
                  excludedFiles={excludedFiles}
                  downloadDocument={downloadDocument}
                  toggleExcludeDocument={toggleExcludeDocument}
                  failedDocuments={failedDocuments}
                  uploadPartialSuccess={uploadPartialSuccess}
                  classes={classes}
                />
              );
            })}
          <NationalIdentitySection
            data={data}
            user={user}
            classes={classes}
            isOrgRoute={isOrgRoute}
            hasBeenSentToWallex={hasBeenSentToWallex}
            screeningStatus={screeningStatus}
            passportPhotoIsLoading={passportPhotoIsLoading}
            excludedFiles={excludedFiles}
            wallexPassportPhotos={wallexPassportPhotos}
            regtankDocumentPhoto={regtankDocumentPhoto}
            localPassportPhotos={localPassportPhotos}
            openIdentityPreview={openIdentityPreview}
            toggleExcludePassportPhoto={toggleExcludePassportPhoto}
            removeLocalPassportPhoto={removeLocalPassportPhoto}
            handlePassportPhotoUpload={handlePassportPhotoUpload}
          />
          {/* Send button */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <Button
              color={
                hasBeenSentToWallex
                  ? "gray"
                  : uploadPartialSuccess
                    ? "orange"
                    : "blue"
              }
              variant="filled"
              size="md"
              radius="md"
              miw={150}
              onClick={() => setIsConfirmModalOpen(true)}
              disabled={hasBeenSentToWallex}
              leftIcon={
                hasBeenSentToWallex ? (
                  <IconCircleCheck size={24} />
                ) : undefined
              }
            >
              {hasBeenSentToWallex
                ? "Sent to Wallex"
                : uploadPartialSuccess
                  ? "Resend"
                  : "Send"}
            </Button>
          </div>
          {/* Timestamp display */}
          {hasBeenSentToWallex && lastUploadTimestamp && (
            <div
              style={{
                marginTop: "8px",
                fontSize: "13px",
                color: "#666666",
                fontStyle: "italic",
                textAlign: "center", // Added to center the text
                width: "100%", // Ensure the div takes full width for centering to work
              }}
            >
              Onboarding information and documents have been sent to Wallex on{" "}
              {formatLocalDate(lastUploadTimestamp)}
            </div>
          )}
        </div>
      </div>
      <WallexConfirmModal
        isOpen={isConfirmModalOpen}
        isUploading={isUploading}
        isCompleted={isCompleted}
        uploadError={uploadError}
        entityName={
          isOrgRoute ? companyName : `${firstName} ${lastName}`
        }
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={async () => {
          try {
            await performUpload();
            await handleUploadSuccess();
            await refreshDataFromServer();
          } catch (error) {
            handleUploadError(error as WallexUploadError);
          }
        }}
        onClearError={() => setUploadError(null)}
      />

      <IdentityPreviewModal
        opened={identityPreviewOpen}
        onClose={closeIdentityPreview}
        content={identityPreviewContent}
        type={identityPreviewType}
        fileName={identityPreviewFileName}
      />
    </div>
  );
};

export default InfoDocWallex;
